import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 999999;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${props => props.$isOpen ? 'fadeIn 0.3s ease' : 'none'};
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #232B3B 0%, #1a202c 100%);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 215, 0, 0.3);
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      transform: translateY(50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 480px) {
    padding: 30px 20px;
    max-width: calc(100% - 40px);
  }
  
  @media (min-width: 1400px) {
    max-width: 600px;
    padding: 50px;
  }
  
  @media (min-width: 1920px) {
    max-width: 700px;
    padding: 60px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 24px;
  transition: all 0.3s;
  z-index: 1;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 35px;
`;

const ModalTitle = styled.h2`
  color: #ffd700;
  font-size: clamp(24px, 4vw, 32px);
  font-weight: 800;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ModalSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(14px, 2vw, 16px);
  line-height: 1.5;
`;

const ContactSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  transition: all 0.3s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 215, 0, 0.4);
  }
`;

const ContactTitle = styled.h3`
  color: #ffd700;
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 600;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PhoneNumber = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #232B3B;
  text-decoration: none;
  padding: 18px;
  border-radius: 10px;
  font-size: clamp(20px, 3vw, 24px);
  font-weight: 800;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    padding: 15px;
  }
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: clamp(12px, 1.8vw, 14px);
  margin-top: 12px;
  text-align: center;
  line-height: 1.5;
`;

const TextBadge = styled.span`
  background: rgba(255, 215, 0, 0.2);
  color: #ffd700;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  margin-left: 8px;
`;

const EmailLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  text-decoration: none;
  padding: 14px;
  border-radius: 8px;
  font-size: clamp(14px, 2vw, 16px);
  font-weight: 500;
  transition: all 0.3s;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 215, 0, 0.5);
    color: #ffd700;
  }
`;

const ScheduleButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%);
  color: white;
  border: none;
  padding: 18px;
  border-radius: 10px;
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4);
    background: linear-gradient(135deg, #00e5ff 0%, #00acc1 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 30px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SelectionButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 215, 0, 0.3);
  color: white;
  padding: 20px;
  border-radius: 12px;
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 215, 0, 0.15);
    border-color: #ffd700;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  span.emoji {
    font-size: 28px;
  }
  
  span.label {
    font-size: clamp(14px, 2vw, 16px);
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    background: rgba(255, 255, 255, 0.05);
  }
`;

interface PhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  openCalendly: () => void;
}

export const PhoneModal: React.FC<PhoneModalProps> = ({ isOpen, onClose, openCalendly }) => {
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const navigate = useNavigate();

  const getCapPhone = () => {
    const a = '419';
    const b = '309';
    const c = '7797';
    return `${a}-${b}-${c}`;
  };

  const getCapEmail = () => {
    const user = 'cap';
    const domain = 'capassociates';
    const ext = 'net';
    return `${user}@${domain}.${ext}`;
  };

  const getMainPhone = () => {
    return '800-922-1894';
  };

  const handleScheduleClick = () => {
    onClose();
    openCalendly();
  };
  
  const handleClose = () => {
    setSelectedContact(null);
    onClose();
  };
  
  const handleBack = () => {
    setSelectedContact(null);
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>×</CloseButton>
        
        {!selectedContact ? (
          <>
            <ModalHeader>
              <ModalTitle>I want to get in touch with</ModalTitle>
            </ModalHeader>
            
            <SelectionGrid>
              <SelectionButton onClick={() => {
                handleClose();
                navigate('/contact');
              }}>
                <span className="emoji">🏢</span>
                <span className="label">The Main Office</span>
              </SelectionButton>
              
              <SelectionButton onClick={() => setSelectedContact('cap')}>
                <img 
                  src="/Cap/Cap.png" 
                  alt="Cap"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    border: '2px solid #ffd700'
                  }}
                />
                <span className="label">Cap</span>
              </SelectionButton>
            </SelectionGrid>
          </>
        ) : (
          <>
            <BackButton onClick={handleBack}>
              <span>←</span>
              <span>Back</span>
            </BackButton>
            
            {selectedContact === 'main' && (
              <>
                <ModalHeader>
                  <ModalTitle>Main Office</ModalTitle>
                  <ModalSubtitle>
                    Contact our main office for general inquiries
                  </ModalSubtitle>
                </ModalHeader>
                
                <ContactSection>
                  <ContactTitle>
                    📞 Phone
                  </ContactTitle>
                  <PhoneNumber href={`tel:${getMainPhone()}`}>
                    <span>📞</span>
                    <span>{getMainPhone()}</span>
                  </PhoneNumber>
                  <InfoText>
                    Monday - Friday: 9AM - 5PM EST
                  </InfoText>
                </ContactSection>
                
                <ScheduleButton onClick={handleScheduleClick}>
                  <span>📅</span>
                  <span>Schedule Consultation</span>
                </ScheduleButton>
              </>
            )}
            
            {selectedContact === 'cap' && (
              <>
                <ModalHeader>
                  <ModalTitle>Cap Averill II</ModalTitle>
                  <ModalSubtitle>
                    Founder & Principal Consultant
                  </ModalSubtitle>
                </ModalHeader>
                
                <ContactSection>
                  <ContactTitle>
                    🔥 Direct Line
                    <TextBadge>TEXT OK</TextBadge>
                  </ContactTitle>
                  <PhoneNumber href={`tel:${getCapPhone()}`}>
                    <span>📱</span>
                    <span>{getCapPhone()}</span>
                  </PhoneNumber>
                  <InfoText>
                    Text or call Cap directly for immediate assistance
                  </InfoText>
                </ContactSection>
                
                <ContactSection>
                  <ContactTitle>
                    ✉️ Direct Email
                  </ContactTitle>
                  <EmailLink href={`mailto:${getCapEmail()}`}>
                    <span>✉️</span>
                    <span>{getCapEmail()}</span>
                  </EmailLink>
                </ContactSection>
                
                <ScheduleButton onClick={handleScheduleClick}>
                  <span>📅</span>
                  <span>Schedule with Cap</span>
                </ScheduleButton>
              </>
            )}
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};